import * as S from './EmbeddedLogin.styles'
import EmbeddedLoginCodeDialog from './Dialogs/EmbeddedLoginCodeDialog'
import EmbeddedLoginDialog from './Dialogs/EmbeddedLoginDialog'
import EmbeddedLoginErrorDialog from './EmbeddedLoginErrorDialog'
import { useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { analytics, EVENT_NAMES } from '../../services/Analytics/Analytics'
import { useAuth } from '../../services/AuthProvider/AuthProvider'
import useOnMount from '../../hooks/useOnMount'
import {
  redirectAfterLogin,
  saveQueryParamsBeforeLogin,
} from '../../services/LoginUtills'
import { useResizeDetector } from 'react-resize-detector'
import useScreenSize from '../../hooks/useScreenSize'
import { intercomService } from '../../services/Analytics/platforms/intercom'
import UserManager from '../../services/UserManager'
import Divider from '../../pages/HomePage/Components/Divider/Divider'
import { useTranslation } from 'react-i18next'

interface ErrorMessage {
  message: string
  title: string
}

interface LoginError {
  error?: string
  errorDescription?: string
}

interface EmbeddedLoginContentProps {
  onLoginProcessStart?: () => void
  source?: string
  onDismiss: () => void
  skipRefresh?: boolean
}

export default function EmbeddedLoginContent({
  onLoginProcessStart,
  source,
  onDismiss,
  skipRefresh = false,
}: EmbeddedLoginContentProps) {
  const { isAuthenticated } = useAuth()
  const [email, setEmail] = useState(UserManager.getUserEmail() || '')
  const [showCode, setShowCode] = useState(false)
  const [errorMessage, setErrorMessage] = useState<ErrorMessage | null>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const { height: contentHeight } = useResizeDetector({
    targetRef: contentRef,
    refreshMode: 'debounce',
    refreshRate: 0,
  })
  const isMediumScreen = useScreenSize('md')
  const { t } = useTranslation()
  const navigate = useNavigate()

  useOnMount(() => {
    if (isAuthenticated) {
      handleAuthentication()
    }
    saveQueryParamsBeforeLogin(window.location.search)
  })

  const handleAuthentication = () => {
    // isVerified is assumed because in embedded login account are always verified.
    return redirectAfterLogin({ navigate, isVerified: true })
  }

  const talkToUsPressed = (screenName: string) => {
    analytics.track(EVENT_NAMES.TALK_TO_US_TAPPED, {
      'Screen Name': screenName,
    })
    intercomService.show()
  }

  const onDismissErrorDialog = () => {
    setErrorMessage(null)
  }

  const showError = (e: LoginError) => {
    const error: ErrorMessage = {
      message: t('login.error_dialog.login_unsuccessful_message'),
      title: t('login.error_dialog.login_unsuccessful_title'),
    }

    if (e.error === 'invalid_grant' || e.error === 'bad.email') {
      error.message = t('login.error_dialog.incorrect_email_or_code_message')
      error.title = t('login.error_dialog.incorrect_email_or_code_title')
    } else if (e.error === 'too_many_attempts') {
      error.message = t(
        'login.error_dialog.too_many_incorrect_attempts_message'
      )
      error.title = t('login.error_dialog.too_many_incorrect_attempts_title')
    }

    const analyticsError = {
      'Error Code': e.error,
      'Error Message': e.errorDescription,
      Error: e.error || undefined,
    }

    analytics.track(EVENT_NAMES.LOGIN_ERROR, analyticsError)
    setErrorMessage(error)
  }

  const dialogsProps = {
    showError,
    showCode,
    setShowCode,
    email,
    setEmail,
    source,
    isMediumScreen,
    onLoginProcessStart,
    onDismiss,
    skipRefresh,
  }

  return (
    <>
      <Divider transparent margin={showCode ? 10 : 20} />
      <S.MainContainer $height={contentHeight}>
        <S.ContentContainer ref={contentRef}>
          {showCode && (
            <S.TopContainer>
              <S.BackButton onClick={() => setShowCode(false)} />
            </S.TopContainer>
          )}
          <S.ViewContainer>
            {showCode ? (
              <EmbeddedLoginCodeDialog {...dialogsProps} />
            ) : (
              <EmbeddedLoginDialog {...dialogsProps} />
            )}
          </S.ViewContainer>
          <EmbeddedLoginErrorDialog
            show={!!errorMessage}
            error={errorMessage}
            onDismiss={onDismissErrorDialog}
            talkToUsPressed={() =>
              talkToUsPressed('Embedded Login Error Dialog')
            }
          />
        </S.ContentContainer>
      </S.MainContainer>
      <Divider
        transparent
        margin={isMediumScreen ? (showCode ? 0 : 16) : showCode ? 0 : 12}
      />
    </>
  )
}
